import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../client';
import { ApiError } from '../errors';
import { camelizeKeys } from '../normalize';

const ACTIVE_STATUSES = new Set([
  'started',
  'preparing',
  'charging',
  'suspendedev',
  'suspendedevse',
  'finishing',
  'stopping',
]);

export interface LiveChargingSession {
  id: string;
  transactionId?: number | null;
  deviceId: string;
  connectorId: number;
  status: string;
  startTime?: string;
  startedAt?: string;
  stopTime?: string | null;
  totalEnergy?: number | null;
  totalEnergyKWh?: number | null;
  activePower?: number | null;
  meanPower?: number | null;
  meanPowerKW?: number | null;
  durationSeconds?: number | null;
  duration?: string | null;
  deviceName?: string | null;
  stationName?: string | null;
  connectorStatus?: string | null;
  idTag?: string | null;
}

function isActiveSession(status: string | undefined | null): boolean {
  return ACTIVE_STATUSES.has(String(status ?? '').toLowerCase());
}

function normalizeLiveSession(raw: unknown): LiveChargingSession {
  const r = camelizeKeys<Record<string, unknown>>(raw);
  return {
    id: String(r.id ?? ''),
    transactionId: typeof r.transactionId === 'number' ? r.transactionId : null,
    deviceId: String(r.deviceId ?? ''),
    connectorId: typeof r.connectorId === 'number' ? r.connectorId : 0,
    status: String(r.status ?? ''),
    startTime: typeof r.startTime === 'string' ? r.startTime : undefined,
    startedAt: typeof r.startedAt === 'string' ? r.startedAt : undefined,
    stopTime: typeof r.stopTime === 'string' ? r.stopTime : null,
    totalEnergy:
      typeof r.totalEnergy === 'number'
        ? r.totalEnergy
        : typeof r.totalEnergyKWh === 'number'
          ? r.totalEnergyKWh
          : null,
    totalEnergyKWh:
      typeof r.totalEnergyKWh === 'number'
        ? r.totalEnergyKWh
        : typeof r.totalEnergy === 'number'
          ? r.totalEnergy
          : null,
    activePower: typeof r.activePower === 'number' ? r.activePower : null,
    meanPower: typeof r.meanPower === 'number' ? r.meanPower : null,
    meanPowerKW: typeof r.meanPowerKW === 'number' ? r.meanPowerKW : null,
    durationSeconds: typeof r.durationSeconds === 'number' ? r.durationSeconds : null,
    duration: typeof r.duration === 'string' ? r.duration : null,
    deviceName: typeof r.deviceName === 'string' ? r.deviceName : null,
    stationName: typeof r.stationName === 'string' ? r.stationName : null,
    connectorStatus: typeof r.connectorStatus === 'string' ? r.connectorStatus : null,
    idTag: typeof r.idTag === 'string' ? r.idTag : null,
  };
}

/** Active power in kW — backend sends activePower in watts. */
export function displayPowerKw(session: LiveChargingSession | null | undefined): number | null {
  if (!session) return null;
  if (typeof session.activePower === 'number' && session.activePower > 0) {
    return session.activePower / 1000;
  }
  if (typeof session.meanPowerKW === 'number' && session.meanPowerKW > 0) {
    return session.meanPowerKW;
  }
  if (typeof session.meanPower === 'number' && session.meanPower > 0) {
    return session.meanPower;
  }
  return null;
}

export function displayEnergyKwh(session: LiveChargingSession | null | undefined): number | null {
  if (!session) return null;
  const v = session.totalEnergyKWh ?? session.totalEnergy;
  return typeof v === 'number' && v > 0 ? v : null;
}

export function useOngoingCharge() {
  return useQuery({
    queryKey: ['my', 'ongoing-charge'],
    queryFn: async (): Promise<LiveChargingSession | null> => {
      try {
        const raw = await api().get<unknown>('/my/ongoing-charge');
        if (!raw || (typeof raw === 'object' && Object.keys(raw as object).length === 0)) {
          return null;
        }
        const obj = raw as Record<string, unknown>;
        if (obj.code === 'not_found') return null;
        return normalizeLiveSession(raw);
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
    refetchInterval: (query) =>
      query.state.data && isActiveSession(query.state.data.status) ? 2_000 : 30_000,
  });
}

export function useMyCharges(params: { page?: number; pageSize?: number } = {}) {
  return useQuery({
    queryKey: ['my', 'charges', params],
    queryFn: async (): Promise<LiveChargingSession[]> => {
      const raw = await api().get<unknown>('/my/charges', { query: params });
      const list = Array.isArray(raw) ? raw : [];
      return list.map((item) => normalizeLiveSession(item));
    },
  });
}

export function useStartCharge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { deviceId: string; connectorId: number; idTag?: string }) =>
      api().post('/my/charges', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my', 'ongoing-charge'] });
      qc.invalidateQueries({ queryKey: ['my', 'charges'] });
      qc.invalidateQueries({ queryKey: ['om', 'sessions'] });
      qc.invalidateQueries({ queryKey: ['om', 'dashboard'] });
    },
  });
}

export function useStopCharge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) => api().patch(`/my/charges/${sessionId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my', 'ongoing-charge'] });
      qc.invalidateQueries({ queryKey: ['my', 'charges'] });
      qc.invalidateQueries({ queryKey: ['om', 'sessions'] });
      qc.invalidateQueries({ queryKey: ['om', 'dashboard'] });
      qc.invalidateQueries({ queryKey: ['admin', 'sessions'] });
    },
  });
}

export { isActiveSession };
